/**
 * Helper utility to synchronize form fields between lookup and ticket forms
 */

/**
 * Dispatch an event to notify that lookup form fields have been updated
 * 
 * @param sessionId The current session ID (call or chat)
 * @param updatedFields Object containing the updated fields
 */
export const notifyLookupFormUpdated = (
    sessionId: string | number | undefined,
    updatedFields: Record<string, any>
) => {
    if (!sessionId || Object.keys(updatedFields).length === 0) return;

    // Map field names from lookup form to ticket form
    const mappedFields: Record<string, any> = {};

    Object.entries(updatedFields).forEach(([key, value]) => {
        // Skip undefined/null/empty values
        if (value === undefined || value === null || value === '') return;

        // Direct field mappings
        const fieldMappings: Record<string, string> = {
            'customerName': 'customerRequestName',
            'customerPhoneNumber': 'customerRequestPhoneNumber',
            'customerAddress': 'customerRequestAddress',
            'districtId': 'district'
            // wardId is handled separately
        };

        // Copy original field
        mappedFields[key] = value;

        // Add mapped field if available
        if (fieldMappings[key]) {
            mappedFields[fieldMappings[key]] = value;
        }

        // Special handling for address
        if (key === 'customerAddress' && value) {
            mappedFields['incidenAddress'] = value;
        }

        // Special handling for district
        if (key === 'districtId' && value) {
            mappedFields['incidentDistrict'] = value;
        }
    });

    // Dispatch event with the updated fields
    window.dispatchEvent(new CustomEvent('lookupFormUpdated', {
        detail: {
            sessionId,
            updatedFields: mappedFields
        }
    }));

};

/**
 * Update minimized ticket modals with new lookup form values
 * 
 * @param sessionId The current session ID
 * @param fieldName The field name that was changed
 * @param value The new value
 * @param wards Optional wards data for resolving ward labels
 */
export const updateMinimizedTicketFields = (
    sessionId: string | number | undefined,
    fieldName: string,
    value: any,
    wards?: Array<{ administrativeUnitId: number; administrativeUnitName: string }>
) => {
    if (!sessionId || value === undefined || value === null || value === '') return;

    try {
        const storedSessions = localStorage.getItem('customerSupportSession');
        if (!storedSessions) return;

        const sessions = JSON.parse(storedSessions);
        const sessionIndex = sessions.findIndex((s: any) =>
            (typeof sessionId === 'string' && s.callId === sessionId) ||
            (typeof sessionId === 'number' && s.chatSessionId === sessionId)
        );

        if (sessionIndex < 0) return;

        let updated = false;
        const session = sessions[sessionIndex];

        // Skip if no searchData
        if (!session.searchData) return;

        // Update all tabs with minimized modals
        for (const tab of session.searchData) {
            // Skip tabs without modals or form data
            if (!tab.minimizedModals?.length || !tab.formData) continue;

            // Update each minimized modal's form data
            for (const modalId of tab.minimizedModals) {
                if (!tab.formData[modalId]) continue;

                const modalData = tab.formData[modalId];

                // Map lookup field names to ticket form field names
                const mappings: Record<string, string> = {
                    'customerName': 'customerRequestName',
                    'customerPhoneNumber': 'customerRequestPhoneNumber',
                    'customerAddress': 'customerRequestAddress',
                    'districtId': 'district',
                    'wardId': 'ward'
                };

                const targetField = mappings[fieldName] || fieldName;

                // Special handling for district/ward fields
                if (fieldName === 'districtId') {
                    // Set district field in form
                    modalData[targetField] = value;

                    // Reset ward fields since district changed
                    if (modalData['ward']) {
                        // Clear both main and incident ward fields
                        modalData['ward'] = undefined;
                        modalData['incidentWard'] = undefined;
                    }

                    // Set incident district to match district
                    modalData['incidentDistrict'] = value;
                    updated = true;
                }
                else if (fieldName === 'wardId') {
                    // For ward ID, we need to maintain object structure with label/value
                    if (typeof value === 'object' && value !== null) {
                        modalData[targetField] = value;
                        modalData['incidentWard'] = value; // Also update incident ward
                    } else if (wards) {
                        // If we only have the ID, we need to find the label from wards array
                        const wardLabel = wards.find(w => w.administrativeUnitId === value)?.administrativeUnitName;
                        if (wardLabel) {
                            const wardObj = { value, label: wardLabel };
                            modalData[targetField] = wardObj;
                            modalData['incidentWard'] = wardObj; // Also update incident ward
                        }
                    }
                    updated = true;
                }
                else if (targetField && targetField !== fieldName) {
                    // For simple field mappings, just update the value
                    modalData[targetField] = value;

                    // For address, also update incident address
                    if (fieldName === 'customerAddress') {
                        modalData['incidenAddress'] = value;
                    }

                    updated = true;
                }
            }
        }

        // Save updated session back to localStorage
        if (updated) {
            localStorage.setItem('customerSupportSession', JSON.stringify(sessions));

            // Dispatch event for listeners
            notifyLookupFormUpdated(sessionId, { [fieldName]: value });

            return true;
        }
    } catch (error) {
        console.error('Error updating minimized ticket fields:', error);
    }

    return false;
};

/**
 * Safely update search criteria without losing form data
 * 
 * @param sessionId The current session ID
 * @param searchCriteria The new search criteria 
 */
export const safeUpdateSearchCriteria = (
    sessionId: string | number | undefined,
    searchCriteria: Record<string, any>
) => {
    if (!sessionId) return;

    try {
        const storedSessions = localStorage.getItem('customerSupportSession');
        if (!storedSessions) return;

        const sessions = JSON.parse(storedSessions);
        const sessionIndex = sessions.findIndex((s: any) =>
            (typeof sessionId === 'string' && s.callId === sessionId) ||
            (typeof sessionId === 'number' && s.chatSessionId === sessionId)
        );

        if (sessionIndex < 0) return;

        const session = sessions[sessionIndex];

        // Update global search criteria
        session.globalSearchCriteria = searchCriteria;

        // Only update searchCriteria in tabs, preserving formData and minimizedModals
        if (session.searchData) {
            session.searchData = session.searchData.map((tab: any) => {
                // Update searchCriteria but keep formData and minimizedModals
                return {
                    ...tab,
                    searchCriteria
                };
            });
        }

        // Save updated session back to localStorage
        localStorage.setItem('customerSupportSession', JSON.stringify(sessions));

        return true;
    } catch (error) {
        console.error('Error safely updating search criteria:', error);
        return false;
    }
};

/**
 * Synchronize form data with the latest search criteria
 * This should be called when opening a minimized form
 * 
 * @param sessionId Current session ID
 * @param serviceTypeId The service type ID of the form to update
 * @returns The updated form data with latest search criteria values
 */
export const getLatestFormData = (
    sessionId: string | number | undefined,
    serviceTypeId: number
): any => {
    if (!sessionId) return null;

    try {
        const storedSessions = localStorage.getItem('customerSupportSession');
        if (!storedSessions) return null;

        const sessions = JSON.parse(storedSessions);
        const sessionIndex = sessions.findIndex((s: any) =>
            (typeof sessionId === 'string' && s.callId === sessionId) ||
            (typeof sessionId === 'number' && s.chatSessionId === sessionId)
        );

        if (sessionIndex < 0) return null;

        const session = sessions[sessionIndex];
        if (!session.searchData) return null;

        // Get global search criteria
        const globalCriteria = session.globalSearchCriteria || {};

        // Find form data for this service type - check all tabs
        let formData: any = null;
        let mostRecentTab = null;
        
        // First pass: Look for the most specific match (exact serviceTypeId in formData)
        for (const tab of session.searchData) {
            // Skip if no form data or minimized modals
            if (!tab.formData || !tab.minimizedModals) continue;

            // Check if this tab has our service type ID in formData with exact match
            if (tab.formData[serviceTypeId]) {
                formData = tab.formData[serviceTypeId];
                mostRecentTab = tab;
                break; // Found an exact match, stop searching
            }
        }
        
        // If we didn't find an exact match, check for tabs that have the service type minimized
        if (!formData) {
            for (const tab of session.searchData) {
                if (!tab.minimizedModals?.includes(serviceTypeId)) continue;
                
                // Check through all form data for a serviceTypeId property that matches
                const formEntries = tab.formData || {};
                for (const key in formEntries) {
                    const data = formEntries[key];
                    if (data && data.serviceTypeId === serviceTypeId) {
                        formData = data;
                        mostRecentTab = tab;
                        break;
                    }
                }
                
                if (formData) break; // Found a match, stop searching
            }
        }

        if (!formData && !mostRecentTab) {
            // If no form data or tab found, use only search criteria
            return globalCriteria ? { ...globalCriteria } : null;
        }

        // Use the tab's specific search criteria if available
        const tabSearchCriteria = mostRecentTab?.searchCriteria || {};
        
        // Map common fields from search criteria (lower priority than form data)
        const mappedCriteria: Record<string, any> = {};

        // Map common fields if not in form data
        if (globalCriteria.customerName && !formData?.customerRequestName) {
            mappedCriteria.customerRequestName = globalCriteria.customerName;
        }
        
        if (globalCriteria.customerPhoneNumber && !formData?.customerRequestPhoneNumber) {
            mappedCriteria.customerRequestPhoneNumber = globalCriteria.customerPhoneNumber;
        }
        
        if (globalCriteria.customerAddress && !formData?.customerRequestAddress) {
            mappedCriteria.customerRequestAddress = globalCriteria.customerAddress;
            if (!formData?.incidenAddress) {
                mappedCriteria.incidenAddress = globalCriteria.customerAddress;
            }
        }
        
        if (globalCriteria.districtId && !formData?.district) {
            mappedCriteria.district = globalCriteria.districtId;
            if (!formData?.incidentDistrict) {
                mappedCriteria.incidentDistrict = globalCriteria.districtId;
            }
        }

        // Handle ward ID mapping if not in form data
        if (globalCriteria.wardId && !formData?.ward) {
            // Ward could be an object or just an ID
            if (typeof globalCriteria.wardId === 'object') {
                mappedCriteria.ward = globalCriteria.wardId;
                if (!formData?.incidentWard) {
                    mappedCriteria.incidentWard = globalCriteria.wardId;
                }
            } else {
                // Try to find the label if possible
                try {
                    if (session?.wardData) {
                        const wardLabel = session.wardData.find((w: any) =>
                            w.administrativeUnitId === globalCriteria.wardId
                        )?.administrativeUnitName;

                        if (wardLabel) {
                            const wardObj = { value: globalCriteria.wardId, label: wardLabel };
                            mappedCriteria.ward = wardObj;
                            if (!formData?.incidentWard) {
                                mappedCriteria.incidentWard = wardObj;
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error processing ward data:', error);
                }
            }
        }

        // Create merged data - IMPORTANT: formData takes precedence over mappedCriteria
        const mergedData = {
            ...mappedCriteria,  // 2. Lower priority - mapped fields from search criteria
            ...formData,        // 1. Higher priority - actual form data
        };

        // Ensure serviceTypeId is preserved
        mergedData.serviceTypeId = serviceTypeId;

        return mergedData;
    } catch (error) {
        console.error('Error getting latest form data:', error);
        return null;
    }
};

export default {
    notifyLookupFormUpdated,
    updateMinimizedTicketFields,
    safeUpdateSearchCriteria,
    getLatestFormData
};
